import { useCallback, useEffect, useMemo, useState } from "react"

import { useResolvedPath, useMatch, useNavigate } from "react-router-dom"

import { useMyUserContext } from "@l2r-front/l2r-auth"
import { useDateFormatter, useDateLocale, useTranslation } from "@l2r-front/l2r-i18n"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { Skeleton, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { useUrlParams } from "@l2r-front/l2r-utils"

import { APP_SAFETY } from "../../../../common/constants/appSafety"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { MODULE_INCIDENTS } from "../../../../common/constants/modules"
import { useProjectStateContext } from "../../../../common/contexts/ProjectContext"
import { FILTERS_SEARCH_PARAM_PREFIX, INITIAL_TIMESLOT_FILTER, PAGE_SEARCH_PARAM_PREFIX } from "../../constants/filters"

import { initialStateContext, IncidentsDispatchContext, IncidentsStateContext } from "./IncidentsContext.context"
import { deleteSearchParamsByKeys, getFiltersFromSearchParams, getFiltersSearchParams, getQueryParamsFromFilters } from "./IncidentsContext.helpers"

const SNACKBAR_ERROR_DURATION = 5 * 1000

export const IncidentsContextProvider = (props) => {
    const { children } = props
    const [incidentsState, setIncidentsState] = useState(initialStateContext)
    const { selectedNetwork } = useNetworksStateContext()
    const { projectStartDate, projectEndDate } = useProjectStateContext()
    const locale = useDateLocale()
    const resolvedPath = useResolvedPath(`${MODULE_INCIDENTS}/:uuid/*`)
    const matchUrl = useMatch(resolvedPath.pathname)
    const matchIncident = useMatch(`:slug/${APP_SAFETY}/${MODULE_INCIDENTS}/`)
    const dateFormatter = useDateFormatter()
    const { user } = useMyUserContext()
    const { setSnackbar } = useAlertsDispatchContext()
    const navigate = useNavigate()
    const isMobile = useIsMobileDevice()
    const { t } = useTranslation(I18N_NAMESPACE)
    const { getParams, setParam, setParams } = useUrlParams()

    const userIsStaff = useMemo(() => {
        return user && user.isStaff
    }, [user])

    const initialFilters = useMemo(() => {
        return ({
            date: {
                periodStartDate: projectStartDate,
                periodEndDate: projectEndDate,
                timeSlotStartHour: INITIAL_TIMESLOT_FILTER,
                timeSlotEndHour: INITIAL_TIMESLOT_FILTER,
            },
            tags: [],
        })
    }, [projectStartDate, projectEndDate])

    useEffect(() => {
        setIncidentsState({
            lastIncidentPage: null,
            selectedIncident: null,
            filters: initialFilters,
        })
    }, [selectedNetwork.slug, initialFilters])

    const selectedIncidentUuid = useMemo(() => {
        return matchUrl?.params?.uuid || null
    }, [matchUrl])

    const setLastIncidentPage = useCallback((currentIncidentPage, replace) => {
        setIncidentsState(value => ({
            ...value,
            lastIncidentPage: currentIncidentPage,
        }))
        setParam(PAGE_SEARCH_PARAM_PREFIX, currentIncidentPage, replace)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIncidentsState])

    useEffect(() => {
        if (matchIncident) {
            setLastIncidentPage(1, true)
        }
    }, [matchIncident, setLastIncidentPage])

    useEffect(() => {
        const searchParams = getParams()
        const { filtersFromParams, errorMessage } = getFiltersFromSearchParams(searchParams, initialFilters, userIsStaff, setParams)
        if (errorMessage) {
            setSnackbar({
                label: t(I18N_NAMESPACE, errorMessage),
                duration: SNACKBAR_ERROR_DURATION,
                severity: "warning",
            })
        }
        const existingFiltersFromParams = Boolean(Object.keys(filtersFromParams || {}).length)

        if (existingFiltersFromParams) {
            return setIncidentsState(value => ({
                ...value,
                filters: {
                    ...initialFilters,
                    ...filtersFromParams,
                },
            }))
        }
        return setIncidentsState(value => ({
            ...value,
            filters: initialFilters,
        }))
    }, [getParams, initialFilters, setSnackbar, setParams, t, userIsStaff])

    const setFilters = useCallback((filters) => {
        setIncidentsState(value => ({
            ...value,
            filters: filters,
            lastIncidentPage: 1,
        }))

        const searchParams = getParams()
        const newSearchParams = getFiltersSearchParams(filters, searchParams, dateFormatter, initialFilters)
        newSearchParams.set(PAGE_SEARCH_PARAM_PREFIX, 1)
        setLastIncidentPage(1)
        setParams(newSearchParams)
        return searchParams
    }, [dateFormatter, getParams, initialFilters, setParams, setLastIncidentPage])

    const resetFilters = useCallback(() => {
        setFilters(initialFilters)
        const searchParams = getParams()
        const newSearchParams = deleteSearchParamsByKeys(searchParams, [FILTERS_SEARCH_PARAM_PREFIX + "*", PAGE_SEARCH_PARAM_PREFIX + "*"])

        if (!isMobile) {
            navigate(newSearchParams)
        }
        return newSearchParams
    }, [getParams, initialFilters, isMobile, navigate, setFilters])

    const isReady = useCallback(() => {
        return incidentsState?.filters?.date
    }, [incidentsState])

    const getFiltersQueryParams = useCallback(() => {
        return getQueryParamsFromFilters(incidentsState.filters, locale, dateFormatter, userIsStaff)

    }, [dateFormatter, locale, incidentsState.filters, userIsStaff])

    const dispatchValue = useMemo(() => {
        return { getFiltersQueryParams, isReady, resetFilters, setLastIncidentPage, setFilters }
    }, [getFiltersQueryParams, isReady, resetFilters, setLastIncidentPage, setFilters])

    const stateValue = useMemo(() => {
        return ({
            ...incidentsState,
            initialFilters,
            selectedIncidentUuid,
        })
    }, [incidentsState, initialFilters, selectedIncidentUuid])

    return (
        <IncidentsStateContext.Provider value={stateValue}>
            <IncidentsDispatchContext.Provider value={dispatchValue}>
                {
                    isReady() ? children : <Skeleton />
                }
            </IncidentsDispatchContext.Provider>
        </IncidentsStateContext.Provider>
    )
}

IncidentsContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
}
